import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded';
import { ContentItem, TodoListContentItem, TodoTask } from '../../shared/interfaces';
import { activityRow, activityHeader, activityIconSlot, shimmerText, activityShimmer } from './activityShimmer';
import { TOOL_LABEL } from './ToolActionBlock';
import { formatDuration } from '../../utils/activitySummary';
import { getThinkingScope, splitActivitySteps } from '../../utils/thinkingGroup';

// Quantas etapas a lista mostra: as mais recentes; as antigas viram "+N".
export const MAX_VISIBLE_STEPS = 5;

// Relógio por grupo, em módulo para sobreviver ao remount do fim da geração
// (o subtree do stream é trocado pelo da mensagem salva). Chaves com o chat.
const timingBySig = new Map<string, { start: number; end?: number }>();

const trimMaps = () => {
  if (timingBySig.size > 500) timingBySig.clear();
};

interface StepRow { key: number; label: string }

/** Duas linhas seguidas com o mesmo texto viram uma só (fica a mais recente). */
const dedupeRows = (rows: StepRow[]) => rows.filter((r, k) => k === rows.length - 1
  || r.label.toLowerCase() !== rows[k + 1].label.toLowerCase());

/**
 * Etapas do grupo. No chat compacto o servidor manda `activity_step`
 * ({index, label}): uma etapa por ciclo do agente, com o rótulo escrito por ele
 * (api utils/compact_stream) — o raciocínio nem chega ao front. Sem esses
 * itens (turno salvo, modo comparação), as etapas saem dos próprios itens e as
 * concluídas levam o rótulo da ferramenta.
 */
export function activityRows(items: ContentItem[], pending: boolean) {
  const server = items.filter((it: any) => it && it.type === 'activity_step') as any[];
  const steps = splitActivitySteps(items.filter((it: any) => it && it.type !== 'activity_step'));
  if (server.length) {
    const labels = new Map<number, string>();
    server.forEach((it) => labels.set(it.index, it.label || labels.get(it.index) || ''));
    const maxIndex = Math.max(...Array.from(labels.keys()));
    const rows = Array.from(labels.entries()).filter(([, label]) => label)
      .sort((a, b) => a[0] - b[0]).map(([key, label]) => ({ key, label }));
    return { rows: dedupeRows(rows), count: Math.max(maxIndex + 1, steps.length), live: pending ? maxIndex : -1 };
  }
  const live = pending ? steps.length - 1 : -1;
  // A etapa ao vivo sem rótulo não entra: no começo fica só "Trabalhando".
  const rows = steps.map((step, key) => ({ key, label: fallbackStepLabel(step) }))
    .filter((r) => r.key !== live && r.label !== 'Pensando');
  return { rows: dedupeRows(rows), count: steps.length, live };
}

/** Rótulo imediato de uma etapa enquanto o resumo do modelo não chega. */
export function fallbackStepLabel(step: ContentItem[]): string {
  for (let i = step.length - 1; i >= 0; i--) {
    const it = step[i] as any;
    if (it.type === 'tool_action') {
      const [doing, done] = (TOOL_LABEL as any)[it.tool] || ['Executando', 'Executou'];
      return it.pending ? doing : done;
    }
    if (it.type === 'code_execution') return it.pending ? 'Executando código' : 'Executou código';
    if (it.type === 'search_results') return 'Pesquisando na web';
  }
  return 'Pensando';
}

interface Props {
  /** Chave posicional da mensagem/grupo ("m3-r0"), prefixada pelo chat. */
  persistKey: string;
  /** Raciocínio, ferramentas e frases intermediárias do turno, em ordem. */
  items: ContentItem[];
  /** Ainda trabalhando (o grupo é o indicador vivo do turno). */
  pending: boolean;
  interrupted?: boolean;
  /** Checklist do turno (lista_de_tarefas), mostrado no lugar das etapas. */
  todo?: TodoListContentItem | null;
}

/**
 * Status do turno no chat do Sabiá 5. Uma linha com o que está acontecendo
 * agora e, embaixo, a lista viva das últimas etapas (no máximo
 * MAX_VISIBLE_STEPS). Cada etapa — um ciclo do agente — ganha um texto curto
 * escrito por uma chamada À PARTE ao Sabiá 5 (/api/chat/activity_summary, sem
 * raciocínio): nada disso passa pela conversa nem entra no histórico. Sem
 * raciocínio bruto, sem detalhes de ferramenta: o trabalho continua no
 * servidor, a tela mostra só o resumo. Ao terminar, recolhe para
 * "Trabalhou por X · N etapas". Nada expande: a lista inteira de um turno
 * longo (dezenas de etapas) ficava grande demais.
 *
 * Com checklist (lista_de_tarefas), as tarefas viram a lista: a em andamento
 * leva embaixo a etapa atual.
 */
function ActivityStatus({ persistKey, items, pending, interrupted = false, todo = null }: Props) {
  const scope = getThinkingScope();
  const sig = `${scope}|${persistKey}`;
  const { rows, count: stepCount, live: liveKey } = useMemo(() => activityRows(items, pending), [items, pending]);
  // Enquanto trabalha, as últimas MAX_VISIBLE_STEPS etapas aparecem no chat;
  // ao terminar recolhe.
  const showList = pending;

  // Relógio: início ao vivo, fim quando conclui ("Trabalhou por 2 min 31 s").
  const [timing, setTiming] = useState(() => timingBySig.get(sig) || null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const cur = timingBySig.get(sig);
    if (pending && !cur) {
      trimMaps();
      timingBySig.set(sig, { start: Date.now() });
    } else if (!pending && cur && !cur.end) {
      timingBySig.set(sig, { ...cur, end: Date.now() });
    }
    setTiming(timingBySig.get(sig) || null);
  }, [sig, pending]);
  useEffect(() => {
    if (!pending) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [pending]);

  const hiddenRows = Math.max(0, rows.length - MAX_VISIBLE_STEPS);

  // Quando entra uma etapa, a janela das últimas anda: as que ficam deslizam
  // para cima e a nova surge de baixo, em vez de todas pularem uma linha de uma
  // vez (o pulo parecia a tela rolando sozinha).
  const trackRef = useRef<HTMLDivElement>(null);
  const firstShown = rows[hiddenRows]?.key ?? -1;
  const prevFirst = useRef(firstShown);
  useLayoutEffect(() => {
    const prev = prevFirst.current;
    prevFirst.current = firstShown;
    const track = trackRef.current;
    if (!track || typeof track.animate !== 'function' || !pending || prev < 0 || firstShown <= prev) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const row = track.firstElementChild as HTMLElement | null;
    if (!row) return;
    const from = rows.findIndex((r) => r.key === prev);
    const moved = from < 0 ? 1 : Math.min(MAX_VISIBLE_STEPS, rows.findIndex((r) => r.key === firstShown) - from);
    track.animate(
      [{ transform: `translateY(${moved * (row.offsetHeight + ROW_GAP)}px)` }, { transform: 'none' }],
      { duration: 260, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' },
    );
    // rows é derivado de firstShown nesta mesma renderização
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstShown, pending]);

  if (stepCount === 0 && !pending) return null;

  const tasks = (todo?.tarefas || []).filter((t) => t && t.titulo);
  const doneTasks = tasks.filter((t) => t.status === 'concluida').length;
  // A etapa atual vai embaixo da tarefa em andamento (ou da próxima pendente).
  const inProgress = tasks.findIndex((t) => t.status === 'em_andamento');
  const currentTask = inProgress >= 0 ? inProgress : tasks.findIndex((t) => t.status !== 'concluida');
  const nowLabel = rows.length ? rows[rows.length - 1].label : '';
  const elapsed = timing ? (timing.end ?? now) - timing.start : null;
  const doneLabel = interrupted
    ? 'Interrompido'
    : timing && timing.end
      ? `Trabalhou por ${formatDuration(timing.end - timing.start)}`
      : 'Concluído';

  return (
    <Row data-download-exclude data-testid="activity-status">
      <Header>
        <IconSlot>
          {pending ? <PulseDot data-testid="thinking-progress" /> : <CheckRoundedIcon data-testid="thinking-icon" style={{ fontSize: 15 }} />}
        </IconSlot>
        {pending ? (
          // O que está acontecendo vai na lista; o cabeçalho só diz que trabalha.
          <LiveLabel>Trabalhando</LiveLabel>
        ) : (
          <DoneLabel>{doneLabel}</DoneLabel>
        )}
        {pending && tasks.length > 0 && <Meta>{doneTasks} de {tasks.length} tarefas</Meta>}
        {pending && elapsed !== null && elapsed >= 2000 && <Meta>{formatDuration(elapsed)}</Meta>}
        {!pending && tasks.length > 0 && (
          <Meta>{doneTasks === tasks.length ? `${tasks.length} tarefas` : `${doneTasks} de ${tasks.length} tarefas`}</Meta>
        )}
        {!pending && !tasks.length && stepCount > 0 && (
          <Meta>{stepCount} {stepCount === 1 ? 'etapa' : 'etapas'}</Meta>
        )}
      </Header>
      {showList && tasks.length > 0 && (
        <List data-testid="activity-plan">
          {tasks.map((task, i) => {
            const current = i === currentTask;
            return (
              <div key={i}>
                <Task $status={task.status} $current={current && pending}>
                  <TaskMark>{taskMark(task, current && pending)}</TaskMark>
                  <span>{task.titulo}</span>
                </Task>
                {current && pending && nowLabel && (
                  <SubLine>
                    <SubdirectoryArrowRightRoundedIcon style={{ fontSize: 13 }} />
                    <SubText key={nowLabel}>{nowLabel}</SubText>
                  </SubLine>
                )}
              </div>
            );
          })}
        </List>
      )}
      {showList && rows.length > 0 && !tasks.length && (
        <List>
          {hiddenRows > 0 && (
            <Earlier>+{hiddenRows} {hiddenRows === 1 ? 'etapa anterior' : 'etapas anteriores'}</Earlier>
          )}
          <StepWindow>
            <StepTrack ref={trackRef}>
              {rows.slice(hiddenRows).map((row) => {
                const live = pending && row.key === liveKey;
                return (
                  <Step key={row.key} $live={live} data-testid="activity-step">
                    <StepMark>{live ? <SmallDot /> : <CheckRoundedIcon style={{ fontSize: 13 }} />}</StepMark>
                    <StepText key={row.label} $live={live}>{row.label}</StepText>
                  </Step>
                );
              })}
            </StepTrack>
          </StepWindow>
        </List>
      )}
    </Row>
  );
}

export default ActivityStatus;

const enter = keyframes`
  from { opacity: 0; transform: translateY(5px); filter: blur(2px); }
  to { opacity: 1; transform: none; filter: none; }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--pulse-color); }
  70% { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const breathe = keyframes`
  0%, 100% { transform: scale(0.82); }
  50% { transform: scale(1); }
`;

const Row = styled.div`
  ${activityRow}
`;

const Header = styled.div`
  ${activityHeader}
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;
  user-select: none;
`;

const IconSlot = styled.span`
  ${activityIconSlot}
  opacity: 0.85;
`;

/* Ponto vivo na cor da marca: respira e solta um anel, no lugar do spinner. */
const PulseDot = styled.span`
  --pulse-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(93,168,138,0.55)' : 'rgba(26,60,52,0.35)')};
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.prompt_accent_color || '#1a3c34'};
  animation: ${breathe} 1.6s ease-in-out infinite, ${pulse} 1.6s ease-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

/* Cada resumo novo entra subindo de leve (a key troca a cada texto) e mantém
   o brilho discreto de "ainda trabalhando". */
/* Texto curto e fixo: não encolhe (com min-width 0 o flex cortava em
   "Trabalha…" / "Trabalhou por 1 min 5…"). */
const LiveLabel = styled.span`
  display: inline-block;
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: 500;
  ${shimmerText}
  animation: ${enter} 380ms cubic-bezier(0.2, 0.7, 0.2, 1) both, ${activityShimmer} 4.2s linear infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const DoneLabel = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: 500;
  animation: ${enter} 300ms ease-out both;
`;

const Meta = styled.span`
  flex-shrink: 0;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.55;
`;

/* Lista das últimas etapas: guia vertical fina, marcas alinhadas ao ícone do
   cabeçalho, texto secundário. */
const List = styled.div`
  position: relative;
  margin: 0 0 8px 3px;
  padding: 2px 0 2px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  border-left: 1.5px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  font-size: 0.84rem;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const Earlier = styled.div`
  align-self: flex-start;
  font-size: 0.76rem;
  opacity: 0.5;
`;

const ROW_GAP = 5;

/* Marca de cada tarefa: concluída ✓, em andamento ponto vivo, pendente anel. */
function taskMark(task: TodoTask, live: boolean) {
  if (task.status === 'concluida') return <CheckRoundedIcon style={{ fontSize: 13 }} />;
  if (live) return <SmallDot />;
  return <Ring $active={task.status === 'em_andamento'} />;
}

const Task = styled.div<{ $status: TodoTask['status']; $current: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  line-height: 1.45;
  overflow-wrap: anywhere;
  animation: ${enter} 280ms ease-out both;
  opacity: ${({ $status }) => ($status === 'concluida' ? 0.6 : $status === 'pendente' ? 0.78 : 1)};
  transition: opacity 300ms ease;
  ${({ $current }) => $current && css`font-weight: 500;`}
`;

/* Marca alinhada à primeira linha do título (que pode quebrar). */
const TaskMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  height: 1.45em;
  flex-shrink: 0;
`;

const Ring = styled.span<{ $active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 1.5px solid currentColor;
  opacity: ${({ $active }) => ($active ? 0.9 : 0.55)};
`;

const SubLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 2px 0 0 18px;
  font-size: 0.78rem;
  opacity: 0.7;
  min-width: 0;
  white-space: nowrap;
`;

const SubText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: ${enter} 280ms ease-out both;
`;


/* Recorta a etapa que entra deslizando de baixo. */
const StepWindow = styled.div`
  overflow: hidden;
`;

const StepTrack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ROW_GAP}px;
`;

const Step = styled.div<{ $live: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  animation: ${enter} 320ms ease-out both;
  opacity: ${({ $live }) => ($live ? 1 : 0.72)};
  transition: opacity 300ms ease;
`;

const StepMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 13px;
  flex-shrink: 0;
`;

const SmallDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.prompt_accent_color || '#1a3c34'};
  animation: ${breathe} 1.6s ease-in-out infinite;
`;

const StepText = styled.span<{ $live: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  animation: ${enter} 280ms ease-out both;
  ${({ $live }) => $live && css`font-weight: 500;`}
`;
